import React from 'react';
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';
import { getEst3CostProvenance } from './est3CostProvenance';
import { EST3_LABOUR_RATE_BASES, summarizeLabourComponents } from './est3LabourKinds';

const { Text, Title } = Typography;

export interface EpcEstimateLabourBuildUpPanelProps {
  estimateId: string;
  revisionId: string;
  costLineId: string | null;
  costLine?: Record<string, unknown> | null;
  readOnly?: boolean;
  onChanged?: () => void;
  compact?: boolean;
}

/**
 * EST3 UX2 Wave 5 — Labour crew snapshot + component build-up.
 */
export const EpcEstimateLabourBuildUpPanel: React.FC<EpcEstimateLabourBuildUpPanelProps> = ({
  estimateId,
  revisionId,
  costLineId,
  costLine,
  readOnly,
  onChanged,
  compact,
}) => {
  const qc = useQueryClient();
  const [crewForm] = Form.useForm();
  const [compForm] = Form.useForm();

  const compQ = useQuery({
    queryKey: ['epc-est-lab-comp', estimateId, costLineId],
    queryFn: () => epcEstimationService.listLabourComponents(estimateId, costLineId!),
    enabled: !!estimateId && !!costLineId,
  });

  const crewQ = useQuery({
    queryKey: ['epc-est-crews', estimateId],
    queryFn: () => epcEstimationService.listCrewTemplates(estimateId),
    enabled: !!estimateId,
  });

  const prodQ = useQuery({
    queryKey: ['epc-est-prod', estimateId],
    queryFn: () => epcEstimationService.listProductivityNorms(estimateId),
    enabled: !!estimateId,
  });

  const classesQ = useQuery({
    queryKey: ['epc-est-lab-classes', estimateId],
    queryFn: () => epcEstimationService.listLabourClasses(estimateId),
    enabled: !!estimateId,
  });

  const applyCrewM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.applyCrew(estimateId, costLineId!, { revisionId, ...v }),
    onSuccess: () => {
      crewForm.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-lab-comp', estimateId, costLineId] });
      qc.invalidateQueries({ queryKey: ['epc-est-cost', estimateId, revisionId] });
      onChanged?.();
    },
  });

  const createM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcEstimationService.createLabourComponent(estimateId, costLineId!, { revisionId, ...v }),
    onSuccess: () => {
      compForm.resetFields();
      qc.invalidateQueries({ queryKey: ['epc-est-lab-comp', estimateId, costLineId] });
      qc.invalidateQueries({ queryKey: ['epc-est-cost', estimateId, revisionId] });
      onChanged?.();
    },
  });

  const deleteM = useMutation({
    mutationFn: (componentId: string) =>
      epcEstimationService.deleteLabourComponent(estimateId, costLineId!, componentId, revisionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['epc-est-lab-comp', estimateId, costLineId] });
      qc.invalidateQueries({ queryKey: ['epc-est-cost', estimateId, revisionId] });
      onChanged?.();
    },
  });

  if (!costLineId) {
    return <Empty description="Select a labour cost line to view crew / rate build-up." />;
  }

  const components = compQ.data || [];
  const { byBasis, componentTotal } = summarizeLabourComponents(components);
  const lineProv = costLine ? getEst3CostProvenance(costLine) : null;
  const classOptions = (classesQ.data || []).map((c: any) => ({
    value: c.id,
    label: `${c.code} — ${c.name}`,
  }));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {costLine ? (
        <Descriptions size="small" column={compact ? 1 : 2} bordered>
          <Descriptions.Item label="Cost line">{String(costLine.description || '—')}</Descriptions.Item>
          <Descriptions.Item label="Line total">
            {formatMoney(costLine.total_amount, { display: 'plain', maximumFractionDigits: 2 })}
          </Descriptions.Item>
          <Descriptions.Item label="Productivity factor">
            {String(costLine.productivity_factor ?? '—')}
          </Descriptions.Item>
          {lineProv && lineProv.summary !== '—' ? (
            <Descriptions.Item label="Line provenance" span={2}>
              {lineProv.summary}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      ) : null}

      <Card size="small" title="Rate basis breakdown">
        {byBasis.length === 0 ? (
          <Text type="secondary">No components — apply a crew or add labour rates manually.</Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {byBasis.map((row) => (
              <div
                key={row.basis}
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}
              >
                <Tag>{row.basis}</Tag>
                <Text>{formatMoney(row.amount, { display: 'plain', maximumFractionDigits: 2 })}</Text>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: 600,
                borderTop: '1px solid #f0f0f0',
                paddingTop: 8,
              }}
            >
              <Text>Component total</Text>
              <Text>{formatMoney(componentTotal, { display: 'plain', maximumFractionDigits: 2 })}</Text>
            </div>
          </Space>
        )}
      </Card>

      {!readOnly ? (
        <>
          <Card size="small" title="Apply crew snapshot">
            <Form
              form={crewForm}
              layout="vertical"
              onFinish={(v) => applyCrewM.mutate(v)}
              initialValues={{ hoursPerMember: 8 }}
            >
              <Form.Item name="crewTemplateId" label="Crew template" rules={[{ required: true }]}>
                <Select
                  options={(crewQ.data || []).map((c: any) => ({
                    value: c.id,
                    label: `${c.code} — ${c.name}`,
                  }))}
                  placeholder={
                    crewQ.data?.length ? 'Select crew' : 'Create a crew in the library section below'
                  }
                />
              </Form.Item>
              <Form.Item name="productivityNormId" label="Productivity norm">
                <Select
                  allowClear
                  options={(prodQ.data || []).map((c: any) => ({
                    value: c.id,
                    label: `${c.code} — ${c.name}`,
                  }))}
                />
              </Form.Item>
              <Form.Item name="hoursPerMember" label="Hours / member">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={applyCrewM.isPending} block>
                Apply crew snapshot
              </Button>
            </Form>
          </Card>

          <Card size="small" title="Add labour component">
            <Form
              form={compForm}
              layout="vertical"
              onFinish={(v) => createM.mutate(v)}
              initialValues={{ rateBasis: 'NORMAL', headcount: 1, hours: 8 }}
            >
              <Form.Item name="resourceClassId" label="Resource class" rules={[{ required: true }]}>
                <Select options={classOptions} showSearch optionFilterProp="label" />
              </Form.Item>
              <Form.Item name="rateBasis" label="Rate basis">
                <Select options={EST3_LABOUR_RATE_BASES.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
              <Space wrap style={{ width: '100%' }}>
                <Form.Item name="headcount" label="Headcount">
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item name="hours" label="Hours">
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item name="rate" label="Rate">
                  <InputNumber min={0} placeholder="Blank = library" />
                </Form.Item>
              </Space>
              <Form.Item name="sourceRefLabel" label="Provenance label">
                <Input placeholder="e.g. Union agreement 2026" />
              </Form.Item>
              <Button htmlType="submit" loading={createM.isPending} block>
                Add labour component
              </Button>
            </Form>
          </Card>
        </>
      ) : null}

      <Card
        size="small"
        title={
          <Space>
            <Title level={5} style={{ margin: 0 }}>
              Components
            </Title>
            <Tag>{components.length}</Tag>
          </Space>
        }
      >
        <Table
          size="small"
          rowKey="id"
          loading={compQ.isLoading}
          dataSource={components}
          pagination={components.length > 20 ? { pageSize: 20 } : false}
          scroll={{ x: true }}
          columns={[
            { title: 'Class', dataIndex: 'resource_class_code', width: 80 },
            { title: 'Basis', dataIndex: 'rate_basis', width: 80 },
            { title: 'HC', dataIndex: 'headcount', width: 50 },
            { title: 'Hrs', dataIndex: 'hours', width: 60 },
            {
              title: 'Rate',
              dataIndex: 'rate',
              width: 80,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              width: 90,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
            },
            {
              title: 'Prov.',
              width: 110,
              ellipsis: true,
              render: (_: unknown, row: any) => {
                const p = getEst3CostProvenance(row);
                return p.summary !== '—' ? (
                  <Tag color="blue">{p.summary}</Tag>
                ) : (
                  <Text type="secondary">{row.rate_source || '—'}</Text>
                );
              },
            },
            ...(!readOnly
              ? [
                  {
                    title: '',
                    width: 70,
                    render: (_: unknown, row: any) => (
                      <Popconfirm title="Remove component?" onConfirm={() => deleteM.mutate(row.id)}>
                        <Button size="small" danger loading={deleteM.isPending}>
                          Del
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
    </Space>
  );
};
